import { LoadingButton } from '@mui/lab'
import { Alert, Button, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useAuthPasswordResetRequestMutation } from 'src/api/mutations/signIn'
import BlockedHint from 'src/components/BlockedHint'
import EntryLayout, {
  CONSOLE_STANDFIRST,
} from 'src/components/layout/EntryLayout'
import Page from 'src/components/layout/Page'
import { useBlockedReason } from 'src/components/useBlockedAction'

// ----------------------------------------------------------------------
// Asking for a password reset link.
//
// Whatever address is typed, the answer is the same, so the page cannot be
// used to find out who has an account. It says what will happen if the
// address has a password, and what to do instead if it does not: most of
// the console signs in with Tesla, and there is nothing to reset there.
// ----------------------------------------------------------------------

const Heading = ({ title, body }: { title: string; body: string }) => (
  <Stack spacing={1}>
    <Typography variant="h2">{title}</Typography>
    <Typography variant="body2" color="text.secondary">
      {body}
    </Typography>
  </Stack>
)

const PasswordForgotBody = () => {
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [request, { loading }] = useAuthPasswordResetRequestMutation()
  const typed = email.trim()
  const blocked = useBlockedReason(!typed)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!typed) {
      return
    }
    setError(null)
    try {
      await request({ variables: { input: { email: typed } } })
      setSentTo(typed)
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'That did not go through. Try again.',
      )
    }
  }

  if (sentTo) {
    return (
      <Stack spacing={3}>
        <Heading
          title="Check your email"
          body={`If ${sentTo} has a password for the console, a link to choose a new one is on its way. It works once, for 30 minutes, and you will need the code from your authenticator app, or a recovery code, as well.`}
        />
        <Typography variant="body2" color="text.secondary">
          If you sign in with Tesla, there is no password to reset. Go back and
          use Continue with Tesla.
        </Typography>
        <Button
          component={RouterLink}
          to="/login"
          variant="outlined"
          size="large"
        >
          Back to sign in
        </Button>
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      <Heading
        title="Forgot your password?"
        body="Enter the address you sign in with. If it has a password, we will email a link to choose a new one."
      />

      {error && (
        <Alert severity="error" role="alert">
          {error}
        </Alert>
      )}

      <Stack component="form" onSubmit={submit} spacing={3}>
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={loading}
          fullWidth
          required
        />
        <BlockedHint
          reason={blocked.blocked ? 'Enter your email' : null}
          reasonProps={blocked.reasonProps}
          sx={{ width: '100%' }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            loading={loading}
            aria-disabled={blocked.blocked}
            disabled={blocked.blocked}
            fullWidth
          >
            Send the link
          </LoadingButton>
        </BlockedHint>
        <Button component={RouterLink} to="/login" variant="text">
          Back to sign in
        </Button>
      </Stack>
    </Stack>
  )
}

const PasswordForgotPage = () => (
  <Page title="Forgot password">
    <EntryLayout
      headline="Run your fleet."
      standfirst={CONSOLE_STANDFIRST}
      photos
    >
      <PasswordForgotBody />
    </EntryLayout>
  </Page>
)

export default PasswordForgotPage
